import { GraduationCap, BookOpen, Heart, Users, ImageIcon } from "lucide-react";
import { Link } from "react-router-dom";

// Placeholder slots — replace `image: null` with real workshop photos as they come in.
const WORKSHOPS = [
  {
    id: "methodist-school-workshop",
    title: "Methodist School Girls' Session",
    location: "Ghana",
    image: "/impact-hero.webp",
    caption: "A joyful FA Workshop celebrating menstrual health, confidence and dignity with school children.",
  },
  {
    id: "workshop-2",
    title: "Community Outreach",
    location: "Greater Accra",
    image: "/workshop-community.webp",
    caption: "Product education & Q&A with mothers, community leaders and young girls.",
  },
  {
    id: "workshop-3",
    title: "Partner Schools Programme",
    location: "Ghana",
    image: "/workshop-partner-schools.webp",
    caption: "Sanitary product support and hygiene training in partnership with the Ghana Health Service.",
  },
];

const PILLARS = [
  {
    icon: <GraduationCap className="w-5 h-5" />,
    title: "Education",
    body: "Workshops in classrooms and community halls that teach the biology of menstruation clearly and warmly.",
  },
  {
    icon: <BookOpen className="w-5 h-5" />,
    title: "Awareness",
    body: "Breaking the silence and stigma that keep too many girls out of school during their periods.",
  },
  {
    icon: <Heart className="w-5 h-5" />,
    title: "Product Support",
    body: "Faytex product donations to ensure no girl misses class because she can't afford a pad.",
  },
  {
    icon: <Users className="w-5 h-5" />,
    title: "Partnership",
    body: "Working with schools, NGOs and community leaders to reach every corner of Ghana.",
  },
];

export default function Impact() {
  return (
    <div data-testid="impact-page" className="bg-white">
      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-faytex-ink text-white">
        <div className="absolute inset-0 -z-0">
          <img
            src="/impact-hero.webp"
            alt="Faytex team with school girls at an FA Workshop in Ghana"
            className="w-full h-full object-cover"
            style={{ objectPosition: "center 30%" }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-40">
          <p className="text-xs uppercase tracking-widest text-faytex-pink">Our Impact</p>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-7xl mt-3 tracking-tighter max-w-4xl">
            Empowering girls begins with education.
          </h1>
          <p className="mt-8 text-white/90 max-w-3xl text-lg md:text-2xl font-heading leading-snug">
            Because empowering girls and women begins with education.
          </p>
        </div>
      </section>

      {/* FA WORKSHOPS STORY */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="text-center">
          <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">
            FA Workshops
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl mt-3 tracking-tight">
            More than pads. A promise.
          </h2>
        </div>
        <div className="mt-10 space-y-5 text-lg text-faytex-muted leading-relaxed">
          <p>Creating quality products is only part of our mission.</p>
          <p>
            Across Ghana, Fay International partners with schools, communities and organisations to
            educate girls and women about menstrual health, hygiene and confidence — while helping
            break the stigma surrounding periods.
          </p>
          <p>
            Through educational outreach, community engagement and product support, we are
            committed to ensuring that more girls have the knowledge and confidence to manage their
            menstrual health with dignity.
          </p>
        </div>
      </section>

      {/* PILLARS */}
      <section className="bg-faytex-pinkLight/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">
              How we show up
            </p>
            <h2 className="font-heading text-3xl md:text-4xl mt-3 tracking-tight">
              Four pillars of the FA Workshops programme
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PILLARS.map((p) => (
              <div
                key={p.title}
                data-testid={`impact-pillar-${p.title.toLowerCase()}`}
                className="bg-white rounded-3xl p-7 shadow-soft hover:shadow-hover transition-shadow"
              >
                <div className="w-12 h-12 rounded-2xl bg-faytex-pinkLight text-faytex-pink flex items-center justify-center">
                  {p.icon}
                </div>
                <h3 className="font-heading text-2xl mt-5">{p.title}</h3>
                <p className="mt-3 text-sm text-faytex-muted leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WORKSHOP GALLERY */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">
              Workshops in action
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl mt-3 tracking-tight">
              Reaching schools, one classroom at a time.
            </h2>
          </div>
          <p className="text-sm text-faytex-muted max-w-sm">
            A growing gallery from FA Workshop sessions with school children, community groups and
            partner organisations across Ghana.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {WORKSHOPS.map((w) => (
            <article
              key={w.id}
              data-testid={`impact-workshop-${w.id}`}
              className="rounded-3xl overflow-hidden border border-faytex-pinkLight bg-white shadow-soft hover:shadow-hover transition-shadow"
            >
              <div className="relative aspect-[4/5] bg-gradient-to-br from-faytex-pinkLight via-white to-faytex-pinkLight/50 flex items-center justify-center">
                {w.image ? (
                  <img src={w.image} alt={w.title} className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <div className="flex flex-col items-center gap-3 text-faytex-pink/60 text-center px-6">
                    <ImageIcon className="w-10 h-10" />
                    <p className="text-xs uppercase tracking-widest font-semibold">Photo coming soon</p>
                  </div>
                )}
              </div>
              <div className="p-6">
                <p className="text-[11px] uppercase tracking-widest text-faytex-pink font-semibold">
                  {w.location}
                </p>
                <h3 className="font-heading text-xl mt-1">{w.title}</h3>
                <p className="mt-2 text-sm text-faytex-muted leading-relaxed">{w.caption}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-faytex-ink text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24 text-center">
          <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">
            Partner with us
          </p>
          <h2 className="font-heading text-3xl md:text-5xl tracking-tight mt-3">
            Bring an FA Workshop to your school or community.
          </h2>
          <p className="mt-6 text-white/85 leading-relaxed">
            If you run a school, NGO or community programme and would like to host an FA Workshop,
            we&rsquo;d love to hear from you.
          </p>
          <Link
            to="/b2b"
            data-testid="impact-cta"
            className="inline-flex items-center gap-2 mt-8 rounded-full bg-faytex-pink hover:bg-faytex-pinkHover px-7 py-3.5 text-sm font-semibold transition-colors"
          >
            Get in touch
          </Link>
        </div>
      </section>
    </div>
  );
}
